import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Container from '../components/common/Container';
import KnowledgeGraphPreview from '../components/features/KnowledgeGraphPreview';
import AdaptiveLearningPreview from '../components/features/AdaptiveLearningPreview';
import UnifiedDashboardPreview from '../components/features/UnifiedDashboardPreview';
import MasteryTrackingPreview from '../components/features/MasteryTrackingPreview';
import CurriculumMapPreview from '../components/curriculum/CurriculumMapPreview';
import CurriculumMapModal from '../components/curriculum/CurriculumMapModal';
import {
  ChartPieIcon,
  CpuChipIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  ArrowPathIcon,
  LightBulbIcon,
  ChartBarIcon,
  BoltIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

const Features: React.FC = () => {
  const [isCurriculumModalOpen, setIsCurriculumModalOpen] = useState(false);

  const coreFeatures = [
    {
      icon: ChartPieIcon,
      title: 'Knowledge Graph Engine',
      subtitle: 'Living Competency Tracking',
      description: 'Every curriculum concept is modeled as an interconnected node that tracks mastery, decay, and reinforcement in real-time.',
      capabilities: [
        'Visual mastery mapping across all subjects',
        'Automatic decay modeling based on cognitive science',
        'Spaced repetition scheduling',
        'Prerequisite dependency tracking',
      ],
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
      visualLabel: 'Knowledge Graph Visualization',
      visualDescription: 'Interactive curriculum node mapping',
      previewType: 'knowledge-graph' as const,
    },
    {
      icon: CpuChipIcon,
      title: 'AI-Powered Adaptation',
      subtitle: 'Personalized Learning Paths',
      description: 'Intelligent algorithms continuously adjust difficulty, pacing, and content selection based on individual performance patterns.',
      capabilities: [
        'Real-time difficulty calibration',
        'Adaptive question selection',
        'Performance pattern recognition',
        'Optimal challenge zone maintenance',
      ],
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
      visualLabel: 'Adaptive Learning Demo',
      visualDescription: 'See how difficulty adjusts in real-time',
      previewType: 'adaptive-learning' as const,
    },
    {
      icon: UserGroupIcon,
      title: 'Unified Dashboard System',
      subtitle: 'Role-Specific Experiences',
      description: 'One platform with tailored interfaces for students, teachers, parents, and administrators - each seeing exactly what they need.',
      capabilities: [
        'Student progress visualization',
        'Teacher intervention tools',
        'Parent transparency portal',
        'Administrator analytics suite',
      ],
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
      visualLabel: 'Dashboard Screenshots',
      visualDescription: 'Role-specific interface previews',
      previewType: 'unified-dashboard' as const,
    },
  ];

  const learningFeatures = [
    {
      icon: BoltIcon,
      title: 'Instant Feedback Loops',
      description: 'Every interaction updates the knowledge graph immediately, providing continuous visibility into learning progress.',
    },
    {
      icon: ArrowPathIcon,
      title: 'Mastery & Retention Logic',
      description: 'Implements evidence-based mastery learning with decay modeling and spaced repetition for long-term retention.',
    },
    {
      icon: LightBulbIcon,
      title: 'Contextual AI Assistant',
      description: 'Provides hints and explanations during practice mode, never giving answers but guiding understanding.',
    },
    {
      icon: ChartBarIcon,
      title: 'Gamified Engagement',
      description: 'XP system, achievements, streaks, and optional leaderboards based on cognitive motivation research.',
    },
    {
      icon: AcademicCapIcon,
      title: 'Curriculum Alignment',
      description: 'Fully mapped to Australian education standards from Kindergarten to Year 12 with complete coverage.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Bank-level encryption, COPPA compliance, and Australian privacy law adherence for student data protection.',
    },
  ];

  const technicalSpecs = [
    { label: 'Data Security', value: 'Bank-Level', description: 'Encryption at rest & in transit' },
    { label: 'Privacy Compliance', value: 'Australian', description: 'Privacy Act compliant' },
    { label: 'Infrastructure', value: 'Cloud-Native', description: 'Scalable & reliable' },
    { label: 'Data Sovereignty', value: 'Local', description: 'Australian data centres' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Platform <span className="text-gradient">Features</span>
            </h1>
            <p className="text-xl text-[var(--color-light-gray)]">
              Built on cognitive science, powered by AI, designed for real educational impact.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Core Features Deep Dive */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="space-y-20">
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.gradient} p-4`}>
                      <feature.icon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">{feature.title}</h2>
                      <p className="text-[var(--color-electric-blue)]">{feature.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-lg text-[var(--color-light-gray)] mb-8">
                    {feature.description}
                  </p>
                  <div className="space-y-4">
                    {feature.capabilities.map((capability) => (
                      <div key={capability} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-[var(--color-electric-blue)] mt-2 flex-shrink-0"></div>
                        <p className="text-[var(--color-light-gray)]">{capability}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  {/* Interactive Preview Components */}
                  <div className="flex justify-center">
                    {feature.previewType === 'knowledge-graph' && (
                      <KnowledgeGraphPreview />
                    )}
                    {feature.previewType === 'adaptive-learning' && (
                      <AdaptiveLearningPreview />
                    )}
                    {feature.previewType === 'unified-dashboard' && (
                      <UnifiedDashboardPreview />
                    )}
                  </div>
                  <div className={`absolute -top-4 -right-4 w-32 h-32 bg-gradient-to-br ${feature.gradient} rounded-full filter blur-3xl opacity-20`}></div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Learning Features Grid */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Learning & Engagement Features
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Every feature is designed to maximize learning outcomes while keeping students engaged and motivated.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {learningFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow">
                  <feature.icon className="w-12 h-12 text-[var(--color-electric-blue)] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-[var(--color-light-gray)]">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Platform Demonstrations */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              See It <span className="text-gradient">In Action</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Watch how Lumen8 transforms the learning experience with real-time mastery tracking and adaptive content.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Mastery Tracking Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="card-premium overflow-hidden hover-glow">
                <div className="flex justify-center p-6">
                  <MasteryTrackingPreview />
                </div>
                <div className="p-6 pt-0">
                  <h3 className="text-xl font-bold text-white mb-3">How Mastery Tracking Works</h3>
                  <ul className="space-y-2 text-[var(--color-light-gray)] text-sm">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-electric-blue)] mt-2 flex-shrink-0"></div>
                      <span>Real-time mastery updates as students answer questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-violet)] mt-2 flex-shrink-0"></div>
                      <span>Difficulty automatically adjusts to maintain optimal challenge</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-emerald)] mt-2 flex-shrink-0"></div>
                      <span>Spaced repetition schedules review for long-term retention</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Curriculum Mapping Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="card-premium overflow-hidden hover-glow">
                <div className="flex justify-center p-6">
                  <CurriculumMapPreview onClick={() => setIsCurriculumModalOpen(true)} />
                </div>
                <div className="p-6 pt-0">
                  <h3 className="text-xl font-bold text-white mb-3">Curriculum Structure Visualisation</h3>
                  <ul className="space-y-2 text-[var(--color-light-gray)] text-sm">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-emerald)] mt-2 flex-shrink-0"></div>
                      <span>Complete Australian curriculum mapped from K-12</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-bright-cyan)] mt-2 flex-shrink-0"></div>
                      <span>Prerequisite dependencies visualised as connected nodes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-electric-blue)] mt-2 flex-shrink-0"></div>
                      <span>Multi-subject coverage in one unified view</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="card-premium p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Technical Excellence</h2>
              <p className="text-[var(--color-light-gray)]">
                Enterprise-grade infrastructure built for scale and reliability
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {technicalSpecs.map((spec) => (
                <div key={spec.label} className="text-center">
                  <div className="text-3xl font-black text-gradient mb-2">{spec.value}</div>
                  <div className="text-white font-semibold mb-1">{spec.label}</div>
                  <div className="text-sm text-[var(--color-medium-gray)]">{spec.description}</div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Learning Science Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black text-white mb-8">
                Grounded in <span className="text-gradient">Learning Science</span>
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Ebbinghaus Forgetting Curve</h3>
                  <p className="text-[var(--color-light-gray)]">
                    Our decay algorithms model natural memory fading to optimize review timing for maximum retention.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Desirable Difficulty</h3>
                  <p className="text-[var(--color-light-gray)]">
                    Maintains optimal challenge levels to keep students in their zone of proximal development.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Retrieval Practice</h3>
                  <p className="text-[var(--color-light-gray)]">
                    Active recall strengthens neural pathways more effectively than passive review.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Mastery Learning</h3>
                  <p className="text-[var(--color-light-gray)]">
                    Students achieve proficiency before progression, ensuring solid foundational understanding.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="glass-card p-8 rounded-2xl">
                <BeakerIcon className="w-16 h-16 text-[var(--color-electric-blue)] mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Evidence-Based Approach</h3>
                <p className="text-[var(--color-light-gray)] mb-6">
                  Every algorithm and feature is backed by peer-reviewed educational research and continuously refined through real-world data.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-[var(--color-electric-blue)]">2σ</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">Bloom's Effect Size</div>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-[var(--color-emerald)]">d=0.7</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">Formative Feedback</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Curriculum Map Modal */}
      <CurriculumMapModal
        isOpen={isCurriculumModalOpen}
        onClose={() => setIsCurriculumModalOpen(false)}
      />
    </>
  );
};

export default Features;